import { Identifier, PaginationPayload, SortPayload } from "react-admin";

import { DataKind, Region, Resource } from "../../types";
import apiUtils from "./ApiUtils";
import {
  buildBaseUrlFor,
  computeBackendBaseUrlFor,
  mapToResourceAndRegionOrError,
} from "./BaseUrlForResourceBuilder";
import { getDataKind } from "./DataKindMapper";

interface RefCtx {
  id: Identifier;
  fetchResource: Resource;
  fetchRegion: Region | undefined;
  assocResource: Resource;
  assocRegion: Region | undefined;
  sort: SortPayload;
  pagination: PaginationPayload;
}

type RefHandler = (ctx: RefCtx) => string;

const refKey = (fetch: DataKind, assoc: DataKind): string =>
  `${fetch}:${assoc}`;

const listWithFilter =
  (resource: Resource, filterKey: string): RefHandler =>
  ({ id, pagination, sort }) =>
    `${buildBaseUrlFor(resource)}?${apiUtils.queryParametersStringify({
      filters: apiUtils.formatFilters({ [filterKey]: id }),
      page: pagination.page,
      per_page: pagination.perPage,
      sorts: apiUtils.formatSorts(sort),
    })}`;

const projectsFromToken: RefHandler = ({
  id,
  pagination,
  sort,
  assocResource,
  assocRegion,
}) =>
  `${buildBaseUrlFor(
    assocResource,
    assocRegion,
  )}/${id}/projects?${apiUtils.queryParametersStringify({
    page: pagination.page,
    per_page: pagination.perPage,
    sorts: apiUtils.formatSorts(sort),
  })}`;

const syncErrorsFromToken: RefHandler = ({
  id,
  pagination,
  assocResource,
  assocRegion,
}) =>
  `${buildBaseUrlFor(
    assocResource,
    assocRegion,
  )}/${id}/sync_errors?${apiUtils.queryParametersStringify({
    page: pagination.page,
    per_page: pagination.perPage,
  })}`;

// There are two types of getManyReferences paths:
//
//   charges?filters[0][account_id_eq]=1     (filter-based, supports pagination)
//   users/1/account                         (hierarchical, one-off endpoints)
//
// The former uses macro-based routes on the BE. The latter is for endpoints
// that involve more complex logic and don't suit the macro-based approach.
const MANY_REFERENCES_HANDLERS: Record<string, RefHandler> = {
  [refKey(DataKind.Account, DataKind.User)]: ({ id }) =>
    `${buildBaseUrlFor(Resource.User)}/${id}/account`,

  [refKey(DataKind.Account, DataKind.Company)]: ({ id }) =>
    `${buildBaseUrlFor(Resource.Company)}/${id}/accounts`,

  [refKey(DataKind.AccountOverageNotification, DataKind.Account)]: ({
    id,
    pagination,
  }) =>
    `${buildBaseUrlFor(
      Resource.AccountOverageNotification,
    )}?${apiUtils.queryParametersStringify({
      filters: apiUtils.formatFilters({ account_id_eq: id }),
      page: pagination.page,
      per_page: pagination.perPage,
    })}`,

  [refKey(DataKind.AccountRegion, DataKind.Account)]: ({ id }) =>
    `${computeBackendBaseUrlFor(
      Resource.AccountRegion,
    )}/accounts/${id}/regions`,

  [refKey(DataKind.AccountUserCount, DataKind.Account)]: listWithFilter(
    Resource.AccountUserCount,
    "account_id_eq",
  ),

  [refKey(DataKind.AdminRoleMembership, DataKind.AdminUser)]: listWithFilter(
    Resource.AdminRoleMembership,
    "admin_user_id_eq",
  ),

  [refKey(DataKind.Charge, DataKind.Account)]: listWithFilter(
    Resource.Charge,
    "account_id_eq",
  ),

  [refKey(DataKind.EnterprisePlanInvoice, DataKind.EnterprisePlan)]:
    listWithFilter(Resource.EnterprisePlanInvoice, "enterprise_plan_id_eq"),

  [refKey(DataKind.Project, DataKind.Account)]: ({
    id,
    pagination,
    sort,
    fetchResource,
    fetchRegion,
  }) =>
    `${buildBaseUrlFor(
      fetchResource,
      fetchRegion,
    )}?${apiUtils.queryParametersStringify({
      filters: apiUtils.formatFilters({ account_id_eq: id }),
      page: pagination.page,
      per_page: pagination.perPage,
      sorts: apiUtils.formatSorts(sort),
    })}`,

  [refKey(DataKind.Project, DataKind.User)]: ({
    id,
    pagination,
    sort,
    fetchResource,
    fetchRegion,
  }) =>
    `${computeBackendBaseUrlFor(
      fetchResource,
      fetchRegion,
    )}/users/${id}/projects_info?${apiUtils.queryParametersStringify({
      page: pagination.page,
      per_page: pagination.perPage,
      sorts: apiUtils.formatSorts(sort),
    })}`,

  [refKey(DataKind.Project, DataKind.BoxToken)]: projectsFromToken,
  [refKey(DataKind.Project, DataKind.DropboxToken)]: projectsFromToken,
  [refKey(DataKind.Project, DataKind.MicrosoftToken)]: projectsFromToken,
  [refKey(DataKind.Project, DataKind.SyncError)]: projectsFromToken,

  [refKey(DataKind.SyncError, DataKind.BoxToken)]: syncErrorsFromToken,
  [refKey(DataKind.SyncError, DataKind.DropboxToken)]: syncErrorsFromToken,
  [refKey(DataKind.SyncError, DataKind.MicrosoftToken)]: syncErrorsFromToken,

  [refKey(DataKind.User, DataKind.Account)]: listWithFilter(
    Resource.User,
    "current_user_of_account_id_eq",
  ),

  [refKey(DataKind.User, DataKind.Project)]: ({
    id,
    pagination,
    sort,
    assocResource,
    assocRegion,
  }) =>
    `${buildBaseUrlFor(
      assocResource,
      assocRegion,
    )}/${id}/project_users?${apiUtils.queryParametersStringify({
      page: pagination.page,
      per_page: pagination.perPage,
      sorts: apiUtils.formatSorts(sort),
    })}`,
};

export const getManyReferences = (
  resource: string,
  id: Identifier,
  target: string,
  sort: SortPayload,
  pagination: PaginationPayload,
): string => {
  const { resource: fetchResource, region: fetchRegion } =
    mapToResourceAndRegionOrError(resource);
  const { resource: assocResource, region: assocRegion } =
    mapToResourceAndRegionOrError(target);

  const handler =
    MANY_REFERENCES_HANDLERS[
      `${getDataKind(fetchResource)}:${getDataKind(assocResource)}`
    ];

  if (!handler) {
    throw new Error(
      `Unexpected combination of resource (${resource}) and target (${target})`,
    );
  }

  return handler({
    id,
    fetchResource,
    fetchRegion,
    assocResource,
    assocRegion,
    sort,
    pagination,
  });
};
